import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { financeApi } from "./financeApi";


type OverviewData = {
  totalPayments: number;
  totalPaid: number;
  totalPending: number;
  totalSellerEligible: number;
  totalSellerPending: number;
  totalDriverEligible: number;
  totalDriverPending: number;
  totalDriverPaidOut: number;
  sellerPayoutBatches: number;
  driverPayoutBatches: number;
  sellerPayoutsPending: number;
  sellerPayoutsPaid: number;
  driverPayoutsPending: number;
  driverPayoutsPaid: number;
};

const money = (value: number) => `MWK ${Number(value || 0).toLocaleString()}`;

const Card = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderRadius="xl"
    p={5}
    shadow="sm"
    _dark={{ bg: "gray.800", borderColor: "gray.700" }}
  >
    <Stat>
      <StatLabel>{label}</StatLabel>
      <StatNumber fontSize="2xl">{value}</StatNumber>
    </Stat>
  </Box>
);

const AdminFinanceOverviewPage: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<OverviewData | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const res = await financeApi.getOverview();
        setOverview(res?.data || null);
      } catch (error: any) {
        toast({
          status: "error",
          title: "Failed to load finance overview",
          description: error?.message,
        });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [toast]);

  if (loading) {
    return (
      <VStack py={10}>
        <Spinner />
        <Text>Loading finance overview...</Text>
      </VStack>
    );
  }

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>
        Finance Overview
      </Heading>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <Card label="Total Payments" value={overview?.totalPayments || 0} />
        <Card label="Paid Payments" value={money(overview?.totalPaid || 0)} />
        <Card
          label="Pending Payments"
          value={money(overview?.totalPending || 0)}
        />
        <Card
          label="Seller Eligible"
          value={money(overview?.totalSellerEligible || 0)}
        />
        <Card
          label="Seller Pending"
          value={money(overview?.totalSellerPending || 0)}
        />
        <Card
          label="Driver Eligible"
          value={money(overview?.totalDriverEligible || 0)}
        />
        <Card
          label="Driver Pending"
          value={money(overview?.totalDriverPending || 0)}
        />
        <Card
          label="Driver Paid Out"
          value={money(overview?.totalDriverPaidOut || 0)}
        />
        <Card
          label="Seller Batches"
          value={overview?.sellerPayoutBatches || 0}
        />
        <Card
          label="Driver Batches"
          value={overview?.driverPayoutBatches || 0}
        />
        <Card
          label="Seller Payouts Pending"
          value={money(overview?.sellerPayoutsPending || 0)}
        />
        <Card
          label="Seller Payouts Paid"
          value={money(overview?.sellerPayoutsPaid || 0)}
        />
        <Card
          label="Driver Payouts Pending"
          value={money(overview?.driverPayoutsPending || 0)}
        />
        <Card
          label="Driver Payouts Paid"
          value={money(overview?.driverPayoutsPaid || 0)}
        />
      </SimpleGrid>
    </Box>
  );
};

export default AdminFinanceOverviewPage;